import React, { useEffect, useState, useRef, ChangeEvent } from "react";
import { Form, Button, Col, Row } from "react-bootstrap";
import axios from "axios";
import LoadingContent from "../../../components/LoadingContent";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import Title from "../../../components/Admin/Title";
import useIsAdmin from "../../../hooks/useIsAdmin";
import ErrorPage from "../../ErrorPage";
import { useUser } from "../../../components/UserContext";
import { useNavigate } from "react-router-dom";

interface GP {
  name: string;
  id: number;
  press_conference: string;
  pole_video: string;
  gp_video: string;
  milestones_en: string;
  milestones_es: string;
}

const UpdateMilestones = () => {
  const { access } = useIsAdmin();
  const navigate = useNavigate();
  document.title = "LVF1 - Update Milestones";
  const { user } = useUser() as any;
  const token = window.localStorage.getItem("userLogged");
  const [uploading, setUploading] = useState(false);
  const [dataForm, setDataForm] = useState({
    gp_ini: "",
    gp_fin: "",
  });
  const [progressMessage, setProgressMessage] = useState("");
  const [lastGrandsPrix, setLastGrandsPrix] = useState<GP[]>();

  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/grandprix/getLastGrandsPrix`,
      )
      .then(({ data }) => {
        setLastGrandsPrix(data.nextGrandsPrix);
        setDataForm({
          gp_ini: data.nextGrandsPrix[data.nextGrandsPrix.length - 1].id,
          gp_fin: data.nextGrandsPrix[0].id,
        });
      })
      .catch(({ response }) => {
        throw new Error(response);
      });
  }, []);

  const inputChange = ({ target }: { target: any }) => {
    const { name, value } = target;
    setDataForm({
      ...dataForm,
      [name]: value,
    });
  };

  const submitForm = async (event: any) => {
    if (token == null) return;
    event.preventDefault();
    event.stopPropagation();

    const parsedToken = JSON.parse(token).token;
    setUploading(true);
    setProgressMessage("Initializing milestone updates...");

    try {
      const response = await fetch(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/admin/updateMilestones`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            gp_ini: Number(dataForm.gp_ini),
            gp_fin: Number(dataForm.gp_fin),
            user: user,
            token: parsedToken,
          }),
        },
      );

      if (!response.ok || !response.body) {
        throw new Error("Failed to connect to the server.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = JSON.parse(line.replace("data: ", ""));

            if (data.status === "processing") {
              setProgressMessage(data.message);
            } else if (data.status === "done") {
              toast.success(data.message);
              setUploading(false);
              setProgressMessage("");
              // Opcional: Redirigir si lo requieres
            } else if (data.status === "error" || data.error) {
              toast.error(data.error || "An error occurred");
              setUploading(false);
              setProgressMessage("");
            }
          }
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Error updating milestones");
      setUploading(false);
      setProgressMessage("");
    }
  };

  if (!access) return <ErrorPage />;
  if (lastGrandsPrix == null) return <LoadingContent />;
  return (
    <div className="full-height padding-md app-container flex-center-top page-transition">
      <div className="form-container">
        <Title text="Update Milestones" />
        <small className="mandatory-fields">(*) Required fields</small>
        <Form noValidate onSubmit={submitForm}>
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="6">
              <Form.Label>Starting Grand Prix (*)</Form.Label>
              <Form.Select
                name="gp_ini"
                required
                value={dataForm.gp_ini}
                onChange={(e) => {
                  inputChange(e);
                }}
              >
                {lastGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>

            <Form.Group className="form-group" as={Col} md="6">
              <Form.Label>Ending Grand Prix (*)</Form.Label>
              <Form.Select
                name="gp_fin"
                required
                value={dataForm.gp_fin}
                onChange={(e) => {
                  inputChange(e);
                }}
              >
                {lastGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Row>
          <div className="text-center">
            {uploading ? (
              <div className="d-flex flex-column align-items-center gap-2">
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="spinner-button fa-spin"
                />
                <small className="text-muted-progress">{progressMessage}</small>
              </div>
            ) : (
              <Button variant="primary" type="submit">
                Update Milestones
              </Button>
            )}
          </div>
        </Form>
      </div>
    </div>
  );
};

export default UpdateMilestones;
